import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
// The landing page stays eager so first paint has no lazy flash.
import ProtectedRoute from './components/ProtectedRoute';
import AdSenseProvider from './components/AdSenseProvider';
import AnalyticsProvider from './components/AnalyticsProvider';
import LanguageSync from './components/LanguageSync';
import DiscordRootRedirect from './components/DiscordRootRedirect';
import MobileTabBar from './components/MobileTabBar';
import LeaguePathRemember from './components/LeaguePathRemember';
import { CommandPaletteProvider } from './components/CommandPalette';
// Landing = self-hosted NCAA map (eager). Classic multi-league home at /classic.
import OwnMapHome from './components/ownMap/OwnMapLab';
import './App.css';

const LeagueSelection = lazy(() => import('./components/LeagueSelection'));
const NotFound = lazy(() => import('./components/NotFound'));

// Everything below the landing page is code-split so the homepage no longer
// ships Mapbox GL, the Discord SDK, gifenc, dnd-kit, etc. in the initial chunk.
const MapView = lazy(() => import('./components/MapView'));
const HeroMapView = lazy(() => import('./components/HeroMapView'));
const AllTimeEmpireView = lazy(() => import('./components/AllTimeEmpireView'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const Admin = lazy(() => import('./components/Admin'));
const StatsQuery = lazy(() => import('./components/StatsQuery'));
const DailySocialFeed = lazy(() => import('./components/DailySocialFeed'));
const SiteFeedPage = lazy(() => import('./components/SiteFeedPage'));
const FantasyOnboarding = lazy(() => import('./components/FantasyOnboarding'));
const FantasyPreview = lazy(() => import('./components/FantasyPreview'));
const FantasyMyLeagues = lazy(() => import('./components/FantasyMyLeagues'));
const FantasyPinsPage = lazy(() => import('./components/FantasyPinsPage'));
const ForumHome = lazy(() => import('./components/forum/ForumHome'));
const ForumCategoryView = lazy(() => import('./components/forum/ForumCategoryView'));
const ForumThreadView = lazy(() => import('./components/forum/ForumThreadView'));
const SurvivorHome = lazy(() => import('./components/survivor/SurvivorHome'));
const SurvivorPoolView = lazy(() => import('./components/survivor/SurvivorPoolView'));
const EmpireTreasuryPage = lazy(() => import('./components/treasury/EmpireTreasuryPage'));
const EmpireHub = lazy(() => import('./components/EmpireHub'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const RivalryPage = lazy(() => import('./components/RivalryPage'));
const Chronicle = lazy(() => import('./components/Chronicle'));
const UfcMapView = lazy(() => import('./components/ufc/UfcMapView'));
const NamingWarsLanding = lazy(() => import('./components/namingWars/NamingWarsLanding'));
const NamingWarsMapView = lazy(() => import('./components/namingWars/NamingWarsMapView'));
const AboutPage = lazy(() => import('./components/content/AboutPage'));
const HowItWorksPage = lazy(() => import('./components/content/HowItWorksPage'));
const PrivacyPage = lazy(() => import('./components/content/PrivacyPage'));
const TermsPage = lazy(() => import('./components/content/TermsPage'));
const GuidesIndexPage = lazy(() => import('./components/content/GuidesIndexPage'));
const LeagueGuidePage = lazy(() => import('./components/content/LeagueGuidePage'));
const DiscordActivityPage = lazy(() => import('./components/DiscordActivityPage'));
const WhoOwnsCounty = lazy(() => import('./components/WhoOwnsCounty'));
const FanRoomHome = lazy(() => import('./components/fanRoom/FanRoomHome'));
const FanRoomRoute = lazy(() => import('./components/fanRoom/FanRoomRoute'));
const LeaguesHub = lazy(() => import('./components/hubs/LeaguesHub'));
const PlayHub = lazy(() => import('./components/hubs/PlayHub'));
const MyCountyHub = lazy(() => import('./components/hubs/MyCountyHub'));
const NewsHub = lazy(() => import('./components/hubs/NewsHub'));
const EnlistPage = lazy(() => import('./components/enlist/EnlistPage'));
const EnlistSuccess = lazy(() => import('./components/enlist/EnlistSuccess'));
const SoldierProfile = lazy(() => import('./components/enlist/SoldierProfile'));
const SoldierHub = lazy(() => import('./components/enlist/SoldierHub'));
const ArmyWallPage = lazy(() => import('./components/enlist/ArmyWallPage'));
const LegionPage = lazy(() => import('./components/enlist/LegionPage'));
const HallPage = lazy(() => import('./components/enlist/HallPage'));
const WarRoomPage = lazy(() => import('./components/enlist/WarRoomPage'));
const WarChroniclePage = lazy(() => import('./components/enlist/WarChroniclePage'));
const RecruiterLanding = lazy(() => import('./components/enlist/RecruiterLanding'));
const BattalionPage = lazy(() => import('./components/enlist/BattalionPage'));
const SchedulePage = lazy(() => import('./components/enlist/SchedulePage'));
const PartnerMapPage = lazy(() => import('./partners/PartnerMapPage'));

const RouteFallback: React.FC = () => (
    <div className="loading-container">
        <div className="loading-spinner" />
    </div>
);

function App() {
    return (
        <AuthProvider>
            <AdSenseProvider>
            <LanguageSync />
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                }}
            >
                <AnalyticsProvider>
                <CommandPaletteProvider>
                <DiscordRootRedirect />
                <LeaguePathRemember />
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<OwnMapHome variant="home" />} />
                    <Route path="/classic" element={<LeagueSelection />} />
                    <Route path="/lab/own-map" element={<OwnMapHome variant="lab" />} />
                    <Route path="/p/:partnerSlug" element={<PartnerMapPage />} />
                    <Route path="/leagues" element={<LeaguesHub />} />
                    <Route path="/play" element={<PlayHub />} />
                    <Route path="/my-county" element={<MyCountyHub />} />
                    <Route path="/news" element={<NewsHub />} />
                    <Route path="/league/:leagueId" element={<MapView />} />
                    <Route path="/league/:leagueId/hero" element={<HeroMapView />} />
                    <Route path="/league/:leagueId/all-time-empire" element={<AllTimeEmpireView />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute requireAdmin>
                                <Admin />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/stats" element={<StatsQuery />} />
                    <Route path="/who-owns/:stateSlug/:countySlug" element={<WhoOwnsCounty />} />
                    <Route path="/forum" element={<ForumHome />} />
                    <Route path="/forum/c/:slug" element={<ForumCategoryView />} />
                    <Route path="/forum/t/:threadId" element={<ForumThreadView />} />
                    <Route path="/survivor" element={<SurvivorHome />} />
                    <Route path="/survivor/:slug" element={<SurvivorPoolView />} />
                    <Route path="/treasury" element={<EmpireTreasuryPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route
                        path="/me"
                        element={
                            <ProtectedRoute>
                                <EmpireHub />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/rivalry/:leagueId/:teamId" element={<RivalryPage />} />
                    <Route
                        path="/chronicle"
                        element={
                            <ProtectedRoute>
                                <Chronicle />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/ufc" element={<UfcMapView />} />
                    <Route path="/naming-wars" element={<NamingWarsLanding />} />
                    <Route path="/naming-wars/map" element={<NamingWarsMapView />} />
                    <Route path="/soccer-football" element={<Navigate to="/naming-wars" replace />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/how-it-works" element={<HowItWorksPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/guides" element={<GuidesIndexPage />} />
                    <Route path="/guides/:slug" element={<LeagueGuidePage />} />
                    <Route path="/feed" element={<SiteFeedPage />} />
                    <Route path="/discord" element={<DiscordActivityPage />} />
                    <Route path="/social/daily" element={<DailySocialFeed />} />
                    <Route path="/fantasy/preview" element={<FantasyPreview />} />
                    <Route
                        path="/fantasy/new"
                        element={
                            <ProtectedRoute>
                                <FantasyOnboarding />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/fantasy/leagues"
                        element={
                            <ProtectedRoute>
                                <FantasyMyLeagues />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/fantasy/:leagueId/pins"
                        element={
                            <ProtectedRoute>
                                <FantasyPinsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/fans" element={<FanRoomHome />} />
                    <Route path="/fans/:teamId" element={<FanRoomRoute />} />
                    <Route path="/enlist" element={<EnlistPage />} />
                    <Route path="/enlist/success" element={<EnlistSuccess />} />
                    <Route path="/enlist/army/:teamId" element={<ArmyWallPage />} />
                    <Route path="/soldier" element={<SoldierHub />} />
                    <Route path="/s/:handle" element={<SoldierProfile />} />
                    <Route path="/legion" element={<LegionPage />} />
                    <Route path="/hall/:teamId" element={<HallPage />} />
                    <Route path="/war-room" element={<SchedulePage />} />
                    <Route path="/war-room/:gameId" element={<WarRoomPage />} />
                    <Route path="/chronicle/war" element={<WarChroniclePage />} />
                    <Route path="/r/:code" element={<RecruiterLanding />} />
                    <Route path="/battalion/:slug" element={<BattalionPage />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                <MobileTabBar />
                </CommandPaletteProvider>
                </AnalyticsProvider>
            </BrowserRouter>
            </AdSenseProvider>
        </AuthProvider>
    );
}

export default App;
